// UiState: everything the reference tool's render() writes into the DOM besides the control values — visibility,
// disabled state, warning/note texts, dynamic labels and the formatted result cards. Keys are the HTML element ids,
// so the UI can bind 1:1 (texts in the user's language; strings marked "HTML" contain <sub>/<small> markup).

import type { EngineTexts } from "./ctx";

export type UiVisible = {
  panelNote: boolean; // note "Elemento 1 = pannello a base di legno…" (single shear panel-timber)
  threeNote: boolean; // three-member note (text in texts.threeNote)
  // report "Note metodologiche" paragraphs for the multi-member / panel connections
  notesThree: boolean;
  notesThreeD: boolean;
  notesPanelD: boolean;
  notesSteel3: boolean;
  notesPanel3: boolean;
  notesPanel: boolean;
  sfalsaDueLatiRow: boolean; // checkbox "Sfalsa di 1d le viti dei due lati" (two-sided connections only)
  mat1SpecieField: boolean; // "Specie legnosa" el.1 (hidden for steel)
  mat1SpecieClasseRow: boolean; // specie+classe row (hidden for USER materials)
  mat2SpecieClasseRow: boolean;
  mat1CustomNameField: boolean; // custom name field (USER materials)
  mat2CustomNameField: boolean;
  mat1Warn: boolean; // "ρk N.D. nel database, inserire manualmente"
  mat2Warn: boolean;
  rhomean1Warn: boolean; // "N.D. nel database per questa classe: inserire manualmente"
  rhomean2Warn: boolean;
  mat1RhoHint: boolean; // hint below the material selects (hidden for steel)
  mat1NormalFields: boolean; // specie/classe/rho block (hidden when CLT)
  mat1CltFields: boolean; // CLT sub-menu
  mat2NormalFields: boolean;
  mat2CltFields: boolean;
  clt1LatoHint: boolean; // "Elemento 1 in connessione legno-legno: ammesso solo il lato wide."
  clt2LatoHint: boolean; // text in texts.clt2LatoHint
  cltFilaNote: boolean; // "Elemento 2 in CLT, lato narrow: una sola fila di viti…" (grid section)
  clt1SpessoriModeField: boolean;
  clt1Spessore0RowField: boolean;
  clt1Spessore90Field: boolean;
  clt1ClasseField: boolean;
  clt1UserLayersWrap: boolean; // per-layer rows ("userDefined")
  clt2SpessoriModeField: boolean;
  clt2Spessore0RowField: boolean;
  clt2Spessore90Field: boolean;
  clt2ClasseField: boolean;
  clt2UserLayersWrap: boolean;
  fi1Field: boolean;
  fi2Field: boolean;
  eps1Field: boolean;
  eps2Field: boolean;
  rhok1Field: boolean;
  rhomean1Row: boolean;
  douglas1Field: boolean;
  a4reduce1Field: boolean;
  steel1DistNote: boolean; // steel plate a3,t,1/a4,t,1/a4,c,1 note
  spacing1FieldRow: boolean; // a1,elemento1 / a2,elemento1 fields
  steel1SpacingNote: boolean;
  continuo1Note: boolean;
  continuo2Note: boolean;
  continuo1DisabledNote: boolean; // "non disponibile a θ=0°/180°…"
  continuo2DisabledNote: boolean;
  d1Field: boolean;
  d2Field: boolean;
  etaPlateHwNote: boolean; // HBS PLATE / LBS HARDWOOD EVO a1,a2 ×1,5 note
  rondellaField: boolean; // washer checkbox (only when the screw has compatible washers)
  rondellaCodice: boolean; // washer select (when "Usa rondella" is checked)
  etaMyKWarn: boolean; // My,k missing in the screw DB
  gammaMColonnaWrap: boolean; // NTC2018 column select
  limitGrigliaWarn: boolean; // "— limite disattivato: …"
  violationsCard: boolean; // "Viti fuori inviluppo" card (hidden when the calculation is blocked)
  statVerificaAxRow: boolean; // extraction check row (Fa > 0)
  statVerificaSplitRow: boolean; // splitting check row (three members / two-sided steel-panel)
};

export type UiDisabled = {
  eps1: boolean;
  eps2: boolean;
  rhok1: boolean; // CLT: auto-filled with the layup ρk
  rhok2: boolean;
  spessore1: boolean; // CLT: sum of the layers
  spessore2: boolean;
  b2: boolean; // CLT narrow el.2: b2 = panel thickness
  d2: boolean; // CLT narrow el.2: d2 = b2/2
  continuo1: boolean; // θ = 0°/180°
  continuo2: boolean;
  s11: boolean; // the spacing/distance fields are read-only with "Distanze minime automatiche"
  s12: boolean;
  a4t1: boolean;
  a4c1: boolean;
  a3t1: boolean;
  a4t2: boolean;
  a4c2: boolean;
  a3t2: boolean;
  a3c2: boolean;
  spreadViti: boolean; // only with automatic spacing
  gammaM: boolean; // gammaM automatic
  clt1LatoNarrow: boolean; // "narrow" option of #clt1Lato disabled (and hidden)
  clt2LatoNarrow: boolean;
  etaForo: boolean; // forced to "predrilled" by the ETA rule (enforcePredrillByRule)
};

// null = element hidden
export type UiTexts = {
  etaFamigliaEmptyWarn: string | null;
  etaForoForced: string | null; // reasons of the imposed predrilling
  etaDiametroEmptyWarn: string | null;
  etaLunghezzaEmptyWarn: string | null;
  a4reduce1Warn: string | null;
  a4reduce2Warn: string | null;
  s11Warn: string | null;
  s12Warn: string | null;
  d1Warn: string | null;
  d2Warn: string | null;
  limitaGrigliaRadenteWarn: string | null;
  screwLengthWarn: string | null;
  staggerNote: string | null;
  spreadNote: string | null; // HTML
  clt1Info: string | null; // CLT layup summary (tone in textTone)
  clt2Info: string | null;
  threeNote: string | null;
  clt2LatoHint: string;
  eps1Note: string; // HTML
  eps2Note: string; // HTML
  calcBlockedBanner: string | null; // "Calcolo non eseguito: …"
};

// The labels depending on the connection type (section titles, legend, units) come from the page's translations.
export type UiLabels = {
  alpha1Display: string;
  alpha2Display: string;
  edDisplay: string;
  faDisplay: string;
};

// Native min/max attributes the HTML sets on inputs (null = attribute removed).
export type UiLimits = { d1Min: number | null; d2Min: number | null; rowsMax: number | null; colsMax: number | null };

// Result cards (render()): formatted values; the formulas next to them come from the report module.
export type UiResults = {
  status: "waiting" | "ok" | "bad"; // global badge: In attesa dei dati / Verificato / Non verificato
  statPill: { labelHtml: string; valueHtml: string; ok: boolean | null; formula: "formulaCaricoCombinato" | null };
  statFvRkVite: string; // HTML
  statNef: string; // HTML
  statNefShear: string;
  lblFvRkUnione: string; // HTML
  statFvRkUnione: string; // HTML
  kmodCard: { labelHtml: string; valueHtml: string; formula: "formulaFaxRdUnione" | null };
  statForceF: string; // HTML
  statFvRdUnione: string; // HTML
  statKser: string; // HTML
  statVerifica: { valueHtml: string; ok: boolean };
  statVerificaAx: string | null; // HTML, null = row hidden
  statVerificaSplit: string | null; // HTML, null = row hidden
};

export type ViolationItem = { x: number; y: number; violations: string[] };

export type UiState = {
  visible: UiVisible;
  disabled: UiDisabled;
  texts: UiTexts;
  textTone: { clt1Info: "amber" | "gray"; clt2Info: "amber" | "gray" };
  labels: UiLabels;
  limits: UiLimits;
  results: UiResults | null; // null when compute() could not run
  // "Viti fuori inviluppo": [] = all screws admissible ("Tutte le viti rispettano…"), null = card hidden (blocked)
  violations: ViolationItem[] | null;
};

export function createUiState(t: EngineTexts): UiState {
  return {
    visible: {
      panelNote: false,
      threeNote: false,
      notesThree: false,
      notesThreeD: false,
      notesPanelD: false,
      notesSteel3: false,
      notesPanel3: false,
      notesPanel: false,
      sfalsaDueLatiRow: false,
      mat1SpecieField: true,
      mat1SpecieClasseRow: true,
      mat2SpecieClasseRow: true,
      mat1CustomNameField: false,
      mat2CustomNameField: false,
      mat1Warn: false,
      mat2Warn: false,
      rhomean1Warn: false,
      rhomean2Warn: false,
      mat1RhoHint: true,
      mat1NormalFields: true,
      mat1CltFields: false,
      mat2NormalFields: true,
      mat2CltFields: false,
      clt1LatoHint: false,
      clt2LatoHint: false,
      cltFilaNote: false,
      clt1SpessoriModeField: true,
      clt1Spessore0RowField: true,
      clt1Spessore90Field: false,
      clt1ClasseField: true,
      clt1UserLayersWrap: false,
      clt2SpessoriModeField: true,
      clt2Spessore0RowField: true,
      clt2Spessore90Field: false,
      clt2ClasseField: true,
      clt2UserLayersWrap: false,
      fi1Field: true,
      fi2Field: true,
      eps1Field: true,
      eps2Field: true,
      rhok1Field: true,
      rhomean1Row: true,
      douglas1Field: true,
      a4reduce1Field: true,
      steel1DistNote: false,
      spacing1FieldRow: true,
      steel1SpacingNote: false,
      continuo1Note: false,
      continuo2Note: false,
      continuo1DisabledNote: false,
      continuo2DisabledNote: false,
      d1Field: true,
      d2Field: true,
      etaPlateHwNote: false,
      rondellaField: false,
      rondellaCodice: false,
      etaMyKWarn: false,
      gammaMColonnaWrap: false,
      limitGrigliaWarn: false,
      violationsCard: false,
      statVerificaAxRow: false,
      statVerificaSplitRow: false,
    },
    disabled: {
      eps1: true,
      eps2: true,
      rhok1: false,
      rhok2: false,
      spessore1: false,
      spessore2: false,
      b2: false,
      d2: false,
      continuo1: false,
      continuo2: false,
      s11: true,
      s12: true,
      a4t1: true,
      a4c1: true,
      a3t1: true,
      a4t2: true,
      a4c2: true,
      a3t2: true,
      a3c2: true,
      spreadViti: false,
      gammaM: true,
      clt1LatoNarrow: false,
      clt2LatoNarrow: false,
      etaForo: false,
    },
    texts: {
      etaFamigliaEmptyWarn: null,
      etaForoForced: null,
      etaDiametroEmptyWarn: null,
      etaLunghezzaEmptyWarn: null,
      a4reduce1Warn: null,
      a4reduce2Warn: null,
      s11Warn: null,
      s12Warn: null,
      d1Warn: null,
      d2Warn: null,
      limitaGrigliaRadenteWarn: null,
      screwLengthWarn: null,
      staggerNote: null,
      spreadNote: null,
      clt1Info: null,
      clt2Info: null,
      threeNote: null,
      clt2LatoHint: t.clt.latoHint2,
      eps1Note: t.engine.epsNote.element1,
      eps2Note: t.engine.epsNote.element2(t.elementRefs.element2),
      calcBlockedBanner: null,
    },
    textTone: { clt1Info: "gray", clt2Info: "gray" },
    labels: {
      alpha1Display: "0.0°",
      alpha2Display: "0.0°",
      edDisplay: "5.0",
      faDisplay: "0.0",
    },
    limits: { d1Min: null, d2Min: null, rowsMax: null, colsMax: null },
    results: null,
    violations: null,
  };
}
